import { types } from "mobx-state-tree";
import fetcher from "../utils/fetcher";
import { persist } from "../utils/persist";
import { getErrorMessage } from "../utils/helper";

const UserDetails = types.model("UserDetails", {
  USERNAME: types.optional(types.string, ""),
  FULLNAME: types.optional(types.string, ""),
  ZCR_USERID: types.maybeNull(types.number, 0),
  ZCR_USERB: types.maybeNull(types.number, 0),
  CUSTOMERS: types.frozen(),
  refreshToken: types.optional(types.string, ""),
  accessToken: types.optional(types.string, ""),
  USERLOGIN: types.maybeNull(types.string, ""),
  ZCR_WEBPASSCHNGFLAG: types.maybeNull(types.string, ""),
});

const UserStore = types
  .model("UserStore", {
    userDetails: UserDetails,
    error: types.frozen(),
    isLoading: types.optional(types.boolean, false),
    language: types.optional(types.string, "he"),
    isAuthenticated: types.optional(types.boolean, false),
    isCoralisEmployee: types.optional(types.boolean, false),
    customers: types.frozen(),
  })
  .actions((self) => {
    function logout() {
      self.setUserDetails({});
      setIsAuthenticated(false);
      setIsCoralisEmployee(false);
      window.localStorage.removeItem("coralis-support-user");
      window.localStorage.removeItem("coralis-storeuser");
      window.localStorage.removeItem("coralis_office-user");
    }

    async function login(data) {
      self.setError("");
      self.setUserDetails({});
      setLoading(true);
      window.localStorage.removeItem("coralis-support-user");
      const dataBody = data;
      const response = await fetcher().post(dataBody).from.auth.login();

      self.setLoading(false);

      if (response.status !== 200) {
        self.setIsAuthenticated(false);
        const status = response.status;
        const errorText =
          status === 404
            ? "error:error_failed_login_user_not_exists"
            : status === 401
            ? "error:error_failed_login_wrong_cardential"
            : status === 400
            ? "error:error_failed_user_error_login_req"
            : "error:error_failed_login_req";
        self.setError(errorText);

        return {
          isSucceed: false,
          error: errorText,
        };
      }

      let userDetails = {
        ...data,
        ...response.data,
      };

      self.setIsAuthenticated(true);
      self.setUserDetails(userDetails);
      self.setIsCoralisEmployee(
        userDetails.ZCR_USERID !== null &&
          userDetails.ZCR_ISEMPLOYEE === "Y" &&
          userDetails.CUSTOMERS.some((cust) => cust.CUSTNAME === "100012")
      );
      window.localStorage.setItem(
        "coralis-support-user",
        JSON.stringify({
          userDetails: self.userDetails,
          isAuthenticated: self.isAuthenticated,
          isCoralisEmployee: self.isCoralisEmployee,
        })
      );
      self.setUserDetails({ ...self.userDetails });

      return {
        isSucceed: true,
        data: self.userDetails,
      };
    }

    async function resetPassword(data) {
      self.setError("");
      const response = await fetcher().post(data).from.auth.reset_password();

      if (response.status !== 200) {
        const errorText =
          response.status === 505
            ? "error:error_failed_same_old_pwd"
            : response.status === 404
            ? "error:error_failed_user_not_exists"
            : "error:error_failed_reset_password_req";

        self.setError(getErrorMessage(response, errorText));
        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function sendResetPassword(data) {
      self.setError("");
      const response = await fetcher().post(data).from.auth.send_reset_password();
      if (response.status !== 200) {
        var errorText = "error:error_failed_send_reset_req";
        if (response.status === 404) {
          errorText = "error:error_failed_login_user_not_exists";
        }
        self.setError(getErrorMessage(response, errorText));

        return {
          isSucceed: false,
          error: errorText,
        };
      }
      return {
        isSucceed: true,
      };
    }

    async function getCustomers(data) {
      self.setError("");
      const response = await fetcher().get.from.common.get_customers();
      if (response.status === 200 && response.data) {
        self.setCustomers(response.data);
        return response.data;
      } else {
        self.setError(getErrorMessage(response, "error:error_failed_get_customers"));
      }
    }

    async function getCustomerOrders(data) {
      self.setError("");
      const response = await fetcher().post(data).from.common.get_customer_orders(data);
      if (response.status === 200 && response.data) {
        return response.data;
      } else {
        self.setError(getErrorMessage(response, "error:error_failed_get_customer_orders"));
      }
    }

    async function getEncryptedUser(data) {
      self.setError("");
      const encryptedRes = await fetcher().post(data).from.auth.get_encrypted_user();
      if (encryptedRes?.status === 200 && encryptedRes?.data) {
        return encryptedRes.data.encrypted;
      } else {
        console.log("error_failed_get_encrypted_user_req");
        self.setError("error:error_failed_get_encrypted_user_req");
      }
    }

    async function getPdfReport(data) {
      self.setError("");
      const response = await fetcher().post(data).from.common.get_pdf_report(data);
      if (response.status === 200 && response.data) {
        return response.data;
      } else {
        self.setError(getErrorMessage(response, "error:error_failed_get_customer_orders"));
      }
    }

    function setError(error) {
      self.error = error;
    }

    function setUserDetails(userDetailsData) {
      self.userDetails = { ...self.userDetails, ...userDetailsData };
      self.userDetails.full_name = self.userDetails.first_name + " " + self.userDetails.last_name;
    }

    function setLoading(loading) {
      self.isLoading = loading;
    }

    function setIsAuthenticated(authenticated) {
      self.isAuthenticated = authenticated;
    }

    function setIsCoralisEmployee(coralisEmployee) {
      self.isCoralisEmployee = coralisEmployee;
    }

    function setCustomers(customers) {
      self.customers = customers;
    }

    function setLanguage(lang) {
      self.language = lang;
    }

    return {
      login,
      logout,
      setLoading,
      setUserDetails,
      setError,
      setLanguage,
      setIsAuthenticated,
      setIsCoralisEmployee,
      resetPassword,
      sendResetPassword,
      getCustomers,
      setCustomers,
      getCustomerOrders,
      getPdfReport,
      getEncryptedUser,
    };
  })
  .views((self) => ({
    get isError() {
      return !!self.error;
    },
    getUser() {
      return self.toJSON();
    },
    get vars() {
      return self.userDetails.toJSON();
    },
  }));

const sessionStorageUser = JSON.parse(localStorage.getItem("coralis-support-user"));
const userStore = UserStore.create({
  userDetails: sessionStorageUser?.userDetails || {},
  error: "",
  isLoading: false,
  allContracts: false,
  isAuthenticated: sessionStorageUser?.isAuthenticated || false,
  isCoralisEmployee: sessionStorageUser?.isCoralisEmployee || false,
  customers: [],
});

persist("coralis-storeuser", userStore, {
  storage: window.localStorage,
  jsonify: true,
});

export default userStore;
